import { ChevronDown, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { DocsOperation } from '../../generated/reference';
import { useDocsKeys } from '../../hooks/use-docs-keys';
import { KeyPanel } from '../key-panel/key-panel';
import { MethodBadge } from '../method-badge';

import { TryItForm } from './try-it-form';

/**
 * The endpoint page's right rail (plan 9): which key to call with, then the request builder and
 * the response. The key section starts open until the browser holds a key.
 */
export function TryItPanel({ operation }: { operation: DocsOperation }) {
  const { t } = useTranslation('docs');
  const { selectedKey } = useDocsKeys();
  // Open while no key is selected, until someone toggles it.
  const [toggled, setKeysOpen] = useState<boolean | null>(null);
  const keysOpen = toggled ?? selectedKey === null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-base font-semibold">{t('tryIt.title')}</h2>
        <div className="flex min-w-0 items-center gap-2">
          <MethodBadge method={operation.method} size="xs" />
          <code className="text-muted-foreground truncate font-mono text-xs">{operation.path}</code>
        </div>
      </div>

      <Collapsible open={keysOpen} onOpenChange={setKeysOpen} className="rounded-lg border">
        <CollapsibleTrigger className="hover:bg-accent/50 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium">
          <KeyRound className="text-muted-foreground size-4 shrink-0" aria-hidden />
          {t('keys.title')}
          <span className="text-muted-foreground ml-auto truncate font-mono text-xs font-normal">
            {selectedKey ? `${selectedKey.prefix}…` : t('keys.none')}
          </span>
          <ChevronDown
            className={cn('size-4 shrink-0 transition-transform', keysOpen && 'rotate-180')}
            aria-hidden
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t p-3">
          <KeyPanel />
        </CollapsibleContent>
      </Collapsible>

      <TryItForm key={operation.id} operation={operation} />
    </div>
  );
}
